'use client';

import {
  Autocomplete,
  Box,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useEffect, useMemo, useRef, useState } from 'react';

import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { NumericBulletSection } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/numericBulletSection';
import { StepErrorDialog } from '@/components/dialogs/stepErrorDialog';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { Regions } from '@/data/inventory/regions';

import type { RestoreMongoAtlasPrivateLinkWizardState } from './restoreMongoAtlasPrivateLinkWizardState';

const VPC_ENDPOINT_ID_PATTERN = /^vpce-[a-z0-9]+$/;

export const AddExistingPrivateLink = (
  props: StepProps<RestoreMongoAtlasPrivateLinkWizardState>
) => {
  const { wizardState, setWizardState } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const dal = useDAL();
  const cancelledRef = useRef(false);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const { atlasProjectId, cloudAccountId, region, vpcEndpointId } = wizardState;

  const awsRegions = Regions[Provider.Aws];
  const regionOptions = useMemo(
    () =>
      Object.entries(awsRegions).map(([key, val]) => ({
        id: key,
        label: `${key} — ${val.displayName}`,
      })),
    [awsRegions]
  );

  const isVpcEndpointIdValid = VPC_ENDPOINT_ID_PATTERN.test(vpcEndpointId);

  const canGoNext = () =>
    !isSubmitting &&
    region.trim().length > 0 &&
    vpcEndpointId.trim().length > 0 &&
    isVpcEndpointIdValid;

  const tryAddExisting = async (retries = 0): Promise<void> => {
    const maxRetries = 60; // ~5 minutes at 5s intervals
    try {
      await dal.mongoAtlasPrivateLinks.addExisting({
        cloudAccountId,
        region,
        vpcEndpointId,
      });
      if (!cancelledRef.current) await props.finish();
    } catch (err: unknown) {
      if (cancelledRef.current) return;
      const errorMessage = await extractErrorMessage(err);
      // If Atlas hasn't accepted the endpoint yet, auto-retry
      if (errorMessage.toLowerCase().includes('atlas has not accepted')) {
        if (retries >= maxRetries) {
          throw new Error('Verification timed out. Please try again.');
        }
        setError(new Error(`${errorMessage} — retrying automatically...`));
        await new Promise((r) => setTimeout(r, 5000));
        if (cancelledRef.current) return;
        return tryAddExisting(retries + 1);
      }
      throw new Error(errorMessage);
    }
  };

  const onNextClick = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      await tryAddExisting();
    } catch (err: unknown) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <StepContainer
        stepperIndex={0}
        hideStepper
        stepperLabels={props.stepperLabels}
        canGoNext={canGoNext}
        nextButtonDisabledTooltip={
          isSubmitting
            ? 'Please wait for the request to complete'
            : 'Please fill all required fields'
        }
        nextButtonText='Add Private Link'
        backButtonText='Cancel'
        onBackClick={props.back}
        sx={{ overflow: 'auto', paddingX: '0px', paddingTop: '24px' }}
        onNextClick={onNextClick}
      >
        <Stack divider={<Divider />} gap='24px'>
          <Stack paddingX='40px'>
            <Typography variant='body1'>
              Register an existing VPC endpoint from your restore account with
              your Atlas project.
            </Typography>
          </Stack>
          <Box
            paddingX='min(100px,6vw)'
            paddingTop='16px'
            width='100%'
            height='auto'
          >
            <Stack
              gap='40px'
              divider={<Divider sx={{ borderStyle: 'dashed' }} />}
            >
              <NumericBulletSection
                bulletNumber={1}
                title='Create the private endpoint'
              >
                <BulletItem
                  title='Go to Atlas UI to create a private endpoint in your restore account VPC.'
                  leftContent={
                    <Stack gap='8px'>
                      <Typography variant='body2'>
                        1. Open the{' '}
                        <strong>
                          <a
                            href={`https://cloud.mongodb.com/v2/${atlasProjectId}#/security/network/privateEndpoint`}
                            target='_blank'
                            rel='noopener noreferrer'
                            style={{ textDecoration: 'underline' }}
                          >
                            Private Endpoint page
                          </a>
                        </strong>{' '}
                        in MongoDB Atlas.
                      </Typography>
                      <Typography variant='body2'>
                        2. Click <strong>Add Private Endpoint</strong> and
                        select <strong>AWS</strong>.
                      </Typography>
                      <Typography variant='body2'>
                        3. Select your region and follow the steps to create the
                        private endpoint in your restore account VPC.
                      </Typography>
                      <Typography variant='body2'>
                        4. Copy the <strong>VPC Endpoint ID</strong> (starts
                        with <code>vpce-</code>) and enter it below.
                      </Typography>
                    </Stack>
                  }
                />
              </NumericBulletSection>
              <NumericBulletSection
                bulletNumber={2}
                title='Complete configuration'
              >
                <BulletItem
                  title='Enter the details from the private endpoint you created.'
                  leftContent={
                    <Stack gap='24px'>
                      <Autocomplete
                        options={regionOptions}
                        value={
                          regionOptions.find((r) => r.id === region) || null
                        }
                        onChange={(_, newValue) =>
                          setWizardState({
                            ...wizardState,
                            region: newValue?.id || '',
                          })
                        }
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label='AWS Region'
                            placeholder='Select a region'
                            size='small'
                          />
                        )}
                        disabled={isSubmitting}
                        fullWidth
                        size='small'
                      />
                      <TextField
                        placeholder='vpce-0123456789abcdef0'
                        label='VPC Endpoint ID'
                        value={vpcEndpointId}
                        onChange={(e) =>
                          setWizardState({
                            ...wizardState,
                            vpcEndpointId: e.target.value,
                          })
                        }
                        error={
                          vpcEndpointId.length > 0 && !isVpcEndpointIdValid
                        }
                        helperText={
                          vpcEndpointId.length > 0 && !isVpcEndpointIdValid
                            ? 'Must match format: vpce-<alphanumeric>'
                            : undefined
                        }
                        fullWidth
                        size='small'
                        disabled={isSubmitting}
                      />
                    </Stack>
                  }
                />
              </NumericBulletSection>
            </Stack>
          </Box>
        </Stack>
      </StepContainer>
      <StepErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
        }}
        error={error}
      />
    </>
  );
};
